// biome-ignore-all lint/performance/noBarrelFile: Package entrypoint intentionally re-exports Codex runtime helpers.
import { bridgeAgentControllerConversationRef, buildAdapterCancelRequest, buildExecutableLaunchContract, createSharedControlShell, createSharedDispatchShell, resolveBuiltinAdapterConversationBridgeSpec, } from "@wakeloop/adapter-core";
import { resolveCodexTimeoutMs as resolveCodexTimeoutMsLocal, verifyCodexLaunchContract as verifyCodexLaunchContractLocal, } from "./config/codex-runtime-config.js";
import { invokeCodexAppServer as invokeCodexAppServerLocal, invokeCodexWithSingleContextHeal as invokeCodexWithSingleContextHealLocal, } from "./invoke/codex-invoke.js";
export { resolveCodexTimeoutMs, resolveInstalledCodexLaunchCandidates, resolveInstalledCodexLaunchContract, verifyCodexLaunchContract, } from "./config/codex-runtime-config.js";
export { isLikelyContextLostError } from "./context/codex-context-heal.js";
export { CodexRuntimeError, invokeCodexAppServer, invokeCodexWithSingleContextHeal, } from "./invoke/codex-invoke.js";
export { processCodexStdoutLine } from "./output/codex-output-parser.js";
export { CodexAppServerTransportError, invokeCodexAppServerTransport, verifyCodexAppServerThread, } from "./transport/codex-app-server-transport.js";
export { CodexCliTransportError, invokeCodexCliTransport, } from "./transport/codex-cli-transport.js";
const CODEX_CONVERSATION_BRIDGE_SPEC = resolveBuiltinAdapterConversationBridgeSpec("codex");
const TEST_DISABLE_DEFAULT_INVOKE = Symbol.for("wakeloop.test.disableDefaultInvoke");
export const createCodexAdapter = (options) => {
    const activeAppServerTurns = new Map();
    const registerAppServerTurn = (registration) => {
        const activeTurn = {
            interrupt: registration.interrupt,
            threadId: registration.threadId,
            turnId: registration.turnId,
        };
        activeAppServerTurns.set(registration.taskId, activeTurn);
        return () => {
            if (activeAppServerTurns.get(registration.taskId) === activeTurn) {
                activeAppServerTurns.delete(registration.taskId);
            }
        };
    };
    const sendInterrupt = async (signal) => {
        const activeTurn = activeAppServerTurns.get(signal.taskId);
        if (activeTurn) {
            await activeTurn.interrupt();
            return;
        }
        if (options.sendInterrupt) {
            await options.sendInterrupt(signal);
            return;
        }
        throw Object.assign(new Error("Codex App Server interrupt is not available for this task."), {
            code: "adapter.unsupported",
        });
    };
    const controlShell = createSharedControlShell({
        gracefulTimeoutMs: options.gracefulTimeoutMs,
        sendCancel: options.sendCancel,
        sendInterrupt,
        waitForMs: options.waitForMs,
    });
    const dispatch = createSharedDispatchShell({
        adapterId: "codex",
        bridgeSpec: CODEX_CONVERSATION_BRIDGE_SPEC,
        invoke: options.invoke,
        invokeByMode: resolveCodexInvokeByTransportMode({
            options,
            registerAppServerTurn,
        }),
        resolveDefaultInvoke: () => resolveDefaultCodexInvoke(options),
        runtimeOptions: options,
    });
    return {
        adapterId: "codex",
        cancel: controlShell.cancel,
        dispatch,
        interrupt: (request) => interruptCodexActiveAppServerTurn({
            activeAppServerTurns,
            request,
            fallbackInterrupt: controlShell.interrupt,
        }),
    };
};
export const buildCodexInterruptRequest = (input) => ({
    mode: "graceful",
    reason: input.reason,
    taskId: input.taskId,
});
export const buildCodexCancelRequest = (input) => buildAdapterCancelRequest({
    reason: input.reason,
    taskId: input.taskId,
});
const resolveDefaultCodexInvoke = (options) => {
    const codexTimeoutMs = options.codexTimeoutMs ?? resolveCodexTimeoutMsLocal();
    return async (input) => {
        const launchContract = await resolveCodexDispatchLaunchContract({
            codexBin: options.codexBin,
            inputLaunchContract: input.launchContract,
            optionLaunchContract: options.launchContract,
        });
        const onProcessControlEvent = options.registerRuntimeProcess
            ? (event) => {
                options.registerRuntimeProcess?.({
                    ...event,
                    taskId: input.taskId,
                });
            }
            : undefined;
        const result = await invokeCodexWithSingleContextHealLocal({
            agentControllerPermissionMode: input.agentControllerPermissionMode,
            launchContract,
            codexTimeoutMs,
            codexWorkdir: input.runtimeContext.providerRuntimeWorkingDirectory,
            conversationPolicy: input.conversationPolicy,
            env: input.env,
            executionMode: input.executionMode,
            ...(onProcessControlEvent ? { onProcessControlEvent } : {}),
            ...(input.onPreview ? { onPreview: input.onPreview } : {}),
            ...(input.onRuntimeEvent ? { onRuntimeEvent: input.onRuntimeEvent } : {}),
            prompt: input.prompt,
            streamingMode: input.streamingMode,
            threadId: normalizeOptionalText(input.agentControllerConversationId),
        });
        const agentControllerConversation = CODEX_CONVERSATION_BRIDGE_SPEC
            ? bridgeAgentControllerConversationRef({
                bridgeSpec: CODEX_CONVERSATION_BRIDGE_SPEC,
                agentControllerConversationId: result.threadId,
            })
            : null;
        return {
            contextDelta: {
                agentControllerConversationId: result.threadId,
                refreshed: result.contextHealed,
                ...(agentControllerConversation ? { agentControllerConversation } : {}),
            },
            output: result.output,
            ...(result.telemetry ? { telemetry: result.telemetry } : {}),
        };
    };
};
const resolveCodexInvokeByTransportMode = (input) => {
    if (input.options.invoke || isDefaultInvokeDisabledForTests(input.options)) {
        return input.options.invokeByMode;
    }
    return {
        "codex-app-server": resolveDefaultCodexAppServerInvoke(input),
        ...(input.options.enableLocalCliTransport
            ? { "local-cli": resolveDefaultCodexInvoke(input.options) }
            : {}),
        ...(input.options.invokeByMode ?? {}),
    };
};
const resolveDefaultCodexAppServerInvoke = (input) => {
    const codexTimeoutMs = input.options.codexTimeoutMs ?? resolveCodexTimeoutMsLocal();
    return async (dispatchInput) => {
        const launchContract = await resolveCodexDispatchLaunchContract({
            codexBin: input.options.codexBin,
            inputLaunchContract: dispatchInput.launchContract,
            optionLaunchContract: input.options.launchContract,
        });
        const onProcessControlEvent = input.options.registerRuntimeProcess
            ? (event) => {
                input.options.registerRuntimeProcess?.({
                    ...event,
                    taskId: dispatchInput.taskId,
                });
            }
            : undefined;
        const result = await invokeCodexAppServerLocal({
            agentControllerPermissionMode: dispatchInput.agentControllerPermissionMode,
            launchContract,
            codexTimeoutMs,
            codexWorkdir: dispatchInput.runtimeContext.providerRuntimeWorkingDirectory,
            conversationPolicy: dispatchInput.conversationPolicy,
            env: dispatchInput.env,
            executionMode: dispatchInput.executionMode,
            onActiveTurn: input.registerAppServerTurn,
            ...(onProcessControlEvent ? { onProcessControlEvent } : {}),
            ...(dispatchInput.onPreview
                ? { onPreview: dispatchInput.onPreview }
                : {}),
            ...(dispatchInput.onRuntimeEvent
                ? { onRuntimeEvent: dispatchInput.onRuntimeEvent }
                : {}),
            prompt: dispatchInput.prompt,
            streamingMode: dispatchInput.streamingMode,
            taskId: dispatchInput.taskId,
            threadId: normalizeOptionalText(dispatchInput.agentControllerConversationId),
        });
        const agentControllerConversation = CODEX_CONVERSATION_BRIDGE_SPEC
            ? bridgeAgentControllerConversationRef({
                bridgeSpec: CODEX_CONVERSATION_BRIDGE_SPEC,
                agentControllerConversationId: result.threadId,
            })
            : null;
        return {
            contextDelta: {
                agentControllerConversationId: result.threadId,
                ...(agentControllerConversation ? { agentControllerConversation } : {}),
            },
            output: result.output,
            ...(result.telemetry ? { telemetry: result.telemetry } : {}),
        };
    };
};
async function interruptCodexActiveAppServerTurn(input) {
    const activeTurn = input.activeAppServerTurns.get(input.request.taskId);
    if (!activeTurn) {
        return await input.fallbackInterrupt(input.request);
    }
    const plan = {
        initial: {
            mode: input.request.mode ?? "graceful",
            reason: input.request.reason ?? "service.interrupt",
            taskId: input.request.taskId,
        },
    };
    try {
        await activeTurn.interrupt();
        return {
            accepted: true,
            plan,
            reasonCode: "accepted",
            signalDelivery: "sent",
            taskId: input.request.taskId,
        };
    }
    catch (error) {
        return {
            accepted: false,
            plan,
            reasonCode: "signal.send_failed",
            reasonDetail: toErrorMessage(error),
            signalDelivery: "not_sent",
            taskId: input.request.taskId,
        };
    }
}
async function resolveCodexDispatchLaunchContract(input) {
    const explicitLaunchContract = input.inputLaunchContract ??
        input.optionLaunchContract ??
        (normalizeOptionalText(input.codexBin)
            ? buildExecutableLaunchContract({
                commandPath: normalizeOptionalText(input.codexBin),
            })
            : null);
    if (explicitLaunchContract) {
        const verification = await verifyCodexLaunchContractLocal({
            launchContract: explicitLaunchContract,
        });
        if (verification.ok) {
            return explicitLaunchContract;
        }
        throw Object.assign(new Error(verification.errorMessage), {
            code: "controller.launch.needs_repair",
            errorType: "config",
        });
    }
    throw Object.assign(new Error("Codex launch contract is required for runtime dispatch."), {
        code: "controller.launch.needs_repair",
        errorType: "config",
    });
}
const normalizeOptionalText = (value) => {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 ? normalized : null;
};
const toErrorMessage = (error) => error instanceof Error ? error.message : String(error);
const isDefaultInvokeDisabledForTests = (options) => options[TEST_DISABLE_DEFAULT_INVOKE] ===
    true;
